import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { Overline } from "@/components/ui-bits";
import {
  Rocket, CheckCircle2, Circle, ArrowRight, Loader2, DollarSign,
  Cloud, Radar, Bot, Settings2, PartyPopper, Zap, Mail,
} from "lucide-react";

const GROUP_META = {
  Money: { icon: DollarSign, color: "#10B981" },
  Deploy: { icon: Cloud, color: "#06B6D4" },
  Outreach: { icon: Radar, color: "#8B5CF6" },
  Agents: { icon: Bot, color: "#F59E0B" },
  Config: { icon: Settings2, color: "#64748B" },
};
const GROUP_ORDER = ["Money", "Deploy", "Outreach", "Agents", "Config"];

export default function LaunchReadiness() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [ws, setWs] = useState(null);

  const load = () => api.get("/go-live").then((r) => setData(r.data)).catch(() => {});
  const loadWs = () => api.get("/workspace").then((r) => setWs(r.data)).catch(() => {});
  useEffect(() => { load(); loadWs(); }, []);
  useRealtime(() => { load(); loadWs(); });

  const toggle = async (key, value, label) => {
    setWs((w) => ({ ...w, [key]: value }));
    try {
      await api.patch("/workspace", { [key]: value });
      toast.success(`${label} ${value ? "enabled" : "disabled"}`);
    } catch (e) { toast.error("Could not save setting"); loadWs(); }
  };

  if (!data) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>;

  const pct = Math.round((data.done / data.total) * 100);
  const autoRefill = ws?.builder_auto_refill !== false;
  const reminders = ws?.readiness_reminders !== false;
  const ceiling = ws?.builder_daily_ceiling || 2000;
  const groups = GROUP_ORDER
    .map((g) => ({ name: g, steps: data.steps.filter((s) => s.group === g) }))
    .filter((g) => g.steps.length);

  return (
    <div className="space-y-6" data-testid="launch-readiness-page">
      <div>
        <Overline>Go-Live · Production Readiness</Overline>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Launch Readiness</h1>
        <p className="text-sm text-slate-400 mt-1">Everything standing between you and a real, revenue-earning production workspace — in one place.</p>
      </div>

      <div className="hud-card accent-top rounded-xl p-6" data-testid="launch-summary" style={{ "--accent-line": data.ready ? "#10B981" : "#06B6D4" }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${data.ready ? "bg-emerald-500/15 border border-emerald-500/40" : "bg-cyan-500/10 border border-cyan-500/30"}`}>
              {data.ready ? <PartyPopper className="w-5 h-5 text-emerald-400" /> : <Rocket className="w-5 h-5 text-cyan-400" />}
            </div>
            <div>
              <div className="font-display font-bold text-lg">{data.ready ? "You're production-ready 🚀" : `${data.total - data.done} step${data.total - data.done === 1 ? "" : "s"} left to go live`}</div>
              <div className="text-[11px] font-mono text-slate-500 uppercase tracking-wider" data-testid="launch-progress">{data.done}/{data.total} complete · {pct}%</div>
            </div>
          </div>
          <div className="text-right">
            <div className={`font-display text-3xl font-black ${data.ready ? "text-emerald-400" : "text-cyan-400"}`}>{pct}%</div>
          </div>
        </div>
        <div className="mt-4 h-2 rounded-full bg-slate-800 overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: data.ready ? "#10B981" : "#06B6D4" }} />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {groups.map((g) => {
          const meta = GROUP_META[g.name] || GROUP_META.Config;
          const Icon = meta.icon;
          const doneCount = g.steps.filter((s) => s.done).length;
          const allDone = doneCount === g.steps.length;
          return (
            <div key={g.name} className="hud-card rounded-lg p-5" data-testid={`launch-group-${g.name.toLowerCase()}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4" style={{ color: meta.color }} />
                  <span className="font-display font-bold text-sm">{g.name}</span>
                </div>
                <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${allDone ? "text-emerald-400 bg-emerald-500/10" : "text-slate-400 bg-slate-800/60"}`}>{doneCount}/{g.steps.length}</span>
              </div>
              <div className="space-y-1">
                {g.steps.map((s) => (
                  <button key={s.key} data-testid={`launch-step-${s.key}`} onClick={() => navigate(`/${s.action}`)}
                    className="group w-full flex items-start gap-3 rounded-md px-3 py-2.5 text-left hover:bg-slate-800/40 transition-colors">
                    {s.done
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                      : <Circle className="w-4 h-4 text-slate-600 shrink-0 mt-0.5" />}
                    <span className="min-w-0 flex-1">
                      <span className={`block text-sm font-medium ${s.done ? "text-slate-500 line-through" : "text-slate-100"}`}>{s.label}</span>
                      <span className="block text-[11px] text-slate-500 mt-0.5">{s.hint}</span>
                    </span>
                    {!s.done && <ArrowRight className="w-3.5 h-3.5 text-slate-600 shrink-0 mt-1 group-hover:text-cyan-400 transition-colors" />}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Automation controls */}
      <div className="hud-card rounded-xl p-5 sm:p-6" data-testid="launch-automation">
        <div className="flex items-center gap-2 mb-4">
          <Settings2 className="w-4 h-4 text-cyan-400" />
          <span className="font-display font-bold text-sm">Go-Live Automation</span>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <button data-testid="toggle-auto-refill" onClick={() => toggle("builder_auto_refill", !autoRefill, "Budget auto-refill")}
            className={`text-left rounded-lg border p-4 transition-all ${autoRefill ? "border-emerald-500/40 bg-emerald-500/[0.06]" : "border-slate-800 bg-[#080B11] hover:border-slate-700"}`}>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 font-display font-bold text-[13px]"><Zap className="w-4 h-4 text-amber-400" /> Budget Auto-Refill</span>
              <span className={`w-9 h-5 rounded-full flex items-center transition-colors ${autoRefill ? "bg-emerald-500 justify-end" : "bg-slate-700 justify-start"} px-0.5`}><span className="w-4 h-4 rounded-full bg-white" /></span>
            </div>
            <p className="text-[12px] text-slate-400 mt-2 leading-relaxed">If a legit ship would exceed a builder's daily cap, the cap is auto-raised in steps (up to <b className="text-slate-200">${ceiling.toLocaleString()}/day</b>) so deploys never stall.</p>
          </button>
          <button data-testid="toggle-reminders" onClick={() => toggle("readiness_reminders", !reminders, "Weekly reminders")}
            className={`text-left rounded-lg border p-4 transition-all ${reminders ? "border-emerald-500/40 bg-emerald-500/[0.06]" : "border-slate-800 bg-[#080B11] hover:border-slate-700"}`}>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 font-display font-bold text-[13px]"><Mail className="w-4 h-4 text-cyan-400" /> Weekly Readiness Reminders</span>
              <span className={`w-9 h-5 rounded-full flex items-center transition-colors ${reminders ? "bg-emerald-500 justify-end" : "bg-slate-700 justify-start"} px-0.5`}><span className="w-4 h-4 rounded-full bg-white" /></span>
            </div>
            <p className="text-[12px] text-slate-400 mt-2 leading-relaxed">Every Monday we email you only the go-live steps still left. Stops automatically once you're 100% live.</p>
          </button>
        </div>
      </div>
    </div>
  );
}
